// Tab 3's records: the lenders' API records, API batches and captured codes. Two ways, as hubStore (LH-B2):
//   fixtures (default, every test): persisted in this browser; every action writes a History line through hubStore
//     (and an Alert where the backend would) and keeps the lender's live / draft batch fields in step
//   the API (VITE_HUB_API=1): a cache of the Lender Hub API. An action still changes the screen at once (same rules,
//     src/lib/batch.ts), then its call goes to the API in order (one queue with the lender calls); the API's answer
//     replaces the local copy, and the API writes History, Alerts, the env sync and the lender's batch fields itself.
//     New ids (a batch, an API, a test event) are made here and kept by the API, so a screen can go to them at once.
// The code runs in the sandbox (src/lib/agent.ts); the rules are in src/lib/batch.ts; this file only moves records.
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { APIS, BATCHES, CAPTURED } from '@/fixtures/code'
import { applyBuild, applyMakeLive, applyRollBack, applySave, applyTestRun, batchName, checksumOf, newVersionFrom, nextVersion, ofCode, type LiveChange } from '@/lib/batch'
import { apiFileFor, endpointLabel, endpointTemplate, testsPathOf } from '@/lib/endpoints'
import { routeOf } from '@/lib/manifest'
import { api, apiMode } from '@/lib/hubApi'
import { extraLoads, refreshLender, refreshLog, remote, useHubStore, whenSynced } from '@/store/hubStore'
import type { Batch, CapturedCode, CodeFile, LenderApi, Manifest, TestEvent, TestResult } from '@/types'

const nowIso = () => new Date().toISOString()

/** What Make live / Roll back answer on the API: every batch of the lender, the replaced and retired versions, the workflows that moved. */
export interface HubLiveChange {
  batches: Batch[]
  previous: string | null
  retired: string | null
  moved: string[]
}
const newId = (p: string) => `${p}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`
const enc = encodeURIComponent

export interface CreateApiInput {
  name: string
  /** the endpoint's route in the batch: /v2/soft-offer → the file src/api/v2/soft-offer.js */
  endpoint: string
  method: LenderApi['method']
  path: string
  auth: LenderApi['auth']
  authNote: string
  whitelist: LenderApi['whitelist']
  usedIn: string
  /** BP-1: the product whose batch gets the new endpoint; null = the lender's whole-lender batch (a lender from before) */
  productSlug?: string | null
}

export interface CodeState {
  apis: LenderApi[]
  batches: Batch[]
  captured: CapturedCode[]

  /**
   * BP-1: `product` left out = every batch of the lender (as before); a product slug = that product's batch only; null =
   * the lender's whole-lender batch only.
   */
  batchesOf: (slug: string, product?: string | null) => Batch[]
  draftOf: (slug: string, product?: string | null) => Batch | undefined
  liveOf: (slug: string, product?: string | null) => Batch | undefined

  /** Create API (F2-2): the record + the endpoint file src/api/<route>.js + one test event, in the draft batch (made if none). Returns the batch id. */
  createApi: (slug: string, input: CreateApiInput, who: string) => string
  updateApi: (id: string, patch: Partial<LenderApi>, who: string) => void
  /** Save = one commit. `sha` and `manifest` come from the sandbox when it made the commit and the discovery. */
  save: (batchId: string, files: CodeFile[], tests: TestEvent[], message: string, who: string, opts?: { sha?: string; manifest?: Manifest; commitFiles?: string[] }) => void
  /** the sandbox's discovery replaces the manifest (and the lender's Env tab learns the variables the code reads) */
  setManifest: (batchId: string, manifest: Manifest, who: string) => void
  recordTestRun: (batchId: string, results: TestResult[], who: string) => Batch
  /** fixtures: the tag and the bundle's checksum here; the API: the image, the tests inside it, the bundle, the tag */
  build: (batchId: string, who: string) => Promise<Batch>
  /** fixtures: `moved` = the live workflows that follow the live batch and passed the C1 check (the caller runs it) */
  makeLive: (batchId: string, who: string, moved: string[]) => LiveChange
  rollBack: (batchId: string, who: string, moved: string[]) => LiveChange
  /** the API (LH-B3): C1 on the server, the version's production container started, then the switch; waits for it */
  switchOnHub: (batchId: string, kind: 'live' | 'back') => Promise<HubLiveChange>
  /** BP-1: `productSlug` = which product's batch (left out: the product of `fromId`, else the whole-lender batch) */
  newVersion: (slug: string, fromId: string | null, version: string | null, who: string, productSlug?: string | null) => Batch
  addTestEvent: (batchId: string, test: Omit<TestEvent, 'id'>, who: string) => void
  /** "Add as a test" on a captured code (F2-3): a captured test event in the draft batch; the alert is acknowledged. */
  addCapturedAsTest: (capturedId: string, expectedOutcome: string, who: string, productSlug?: string | null) => string
  /**
   * G11 capture (F3-6): the lender codes a run saw. A known code is counted; a new one that parse() turned into error
   * becomes "new · alert open" with an alert; a new one with a real outcome is recorded as known. Returns the new codes.
   */
  captureCodes: (lenderSlug: string, items: { endpoint: string; code: string; outcome: string; reply: CapturedCode['sampleReply'] }[], env: 'uat' | 'production') => string[]
  reset: () => void
}

// ---- the API side ----------------------------------------------------------------------------------------------

/** fixtures only: History / Alerts / the lender's fields made here; on the API the API makes them */
const local = (fn: () => void) => { if (!apiMode()) fn() }

const putBatch = (b: Batch) => useCodeStore.setState((s) => ({ batches: s.batches.some((x) => x.id === b.id) ? s.batches.map((x) => (x.id === b.id ? b : x)) : [b, ...s.batches] }))
const putBatches = (slug: string, list: Batch[]) => useCodeStore.setState((s) => ({ batches: [...list, ...s.batches.filter((b) => b.lenderSlug !== slug)] }))
const putApi = (a: LenderApi) => useCodeStore.setState((s) => ({ apis: s.apis.some((x) => x.id === a.id) ? s.apis.map((x) => (x.id === a.id ? a : x)) : [...s.apis, a] }))
const putCaptured = (slug: string, list: CapturedCode[]) => useCodeStore.setState((s) => ({ captured: [...list, ...s.captured.filter((c) => c.lenderSlug !== slug)] }))

/** API mode: the call after the earlier ones; its answer applied; the lender and History read again. */
function send<T>(slug: string, call: () => Promise<T>, apply: (res: T) => void) {
  remote(call, (res) => {
    apply(res)
    void refreshLender(slug).catch(() => {})
  })
}

/** Tab 3 read from the API for these lenders (at sign-in, and after a refusal). */
export async function loadCodeFromHub(slugs: string[]) {
  const per = await Promise.all(slugs.map(async (slug) => {
    const [batches, apis, captured] = await Promise.all([
      api<Batch[]>('GET', `/lenders/${enc(slug)}/batches`),
      api<LenderApi[]>('GET', `/lenders/${enc(slug)}/apis`),
      api<CapturedCode[]>('GET', `/lenders/${enc(slug)}/captured`),
    ])
    return { batches, apis, captured }
  }))
  useCodeStore.setState({ batches: per.flatMap((x) => x.batches), apis: per.flatMap((x) => x.apis), captured: per.flatMap((x) => x.captured) })
}
extraLoads.push(loadCodeFromHub)

// ---- fixtures only ------------------------------------------------------------------------------------------------

function syncLender(slug: string, batches: Batch[]) {
  if (apiMode()) return
  const mine = batches.filter((b) => b.lenderSlug === slug)
  const live = mine.find((b) => b.status === 'live')
  const draft = mine.filter((b) => b.status === 'draft' || b.status === 'tests_passed' || b.status === 'built').sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0]
  useHubStore.getState().patchLender(slug, { liveBatch: live?.version ?? null, draftBatch: draft?.version ?? null })
}

const hub = () => useHubStore.getState()

export const useCodeStore = create<CodeState>()(
  persist(
    (set, get) => ({
      apis: apiMode() ? [] : APIS,
      batches: apiMode() ? [] : BATCHES,
      captured: apiMode() ? [] : CAPTURED,

      batchesOf: (slug, product) => (product === undefined ? get().batches.filter((b) => b.lenderSlug === slug) : ofCode(get().batches, slug, product)),
      draftOf: (slug, product) => get().batchesOf(slug, product).filter((b) => b.status === 'draft' || b.status === 'tests_passed').sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0],
      liveOf: (slug, product) => get().batchesOf(slug, product).find((b) => b.status === 'live'),

      createApi: (slug, input, who) => {
        const at = nowIso()
        let batches = get().batches
        const product = input.productSlug ?? null
        let draft = get().draftOf(slug, product)
        let madeDraft: string | null = null
        if (!draft) {
          const from = get().liveOf(slug, product) ?? null
          draft = newVersionFrom(from, slug, nextVersion(ofCode(batches, slug, product)), who, at, product)
          madeDraft = draft.id
          batches = [draft, ...batches]
          local(() => hub().addHistory(who, slug, `Batch ${draft!.version}`, `New version from ${from?.version ?? 'nothing'} (for a new API)`))
        }
        const d = draft
        const route = '/' + input.endpoint.replace(/^\/+/, '').replace(/\/+$/, '')
        const path = apiFileFor(d.files, route)
        if (d.files.some((f) => f.path === path)) throw new Error(`${path} already exists in ${batchName(d)}`)
        if (d.manifest.endpoints.some((e) => e.path === route)) throw new Error(`endpoint ${route} already exists in ${batchName(d)}`)
        if (routeOf(path) !== route) throw new Error(`the route ${route} cannot be a file name (use letters, numbers, - and /)`)
        const files: CodeFile[] = [...d.files, { path, content: endpointTemplate(input.name, input.method, input.path, input.auth) }]
        const tests: TestEvent[] = [...d.tests, { id: newId('t'), endpoint: route, name: 'Reply 200 → ok', reply: { status: 200, body: { id: 'DEMO-1' } }, expectedOutcome: 'ok', source: 'sample' }]
        const saved = applySave(d, files, tests, `Create API ${input.name} (${route})`, who, at)
        const apiRecord: LenderApi = { id: newId('api'), lenderSlug: slug, ...input, endpoint: route, status: 'active', lastCall: null, answer: 'none', fields: [] }
        set({ apis: [...get().apis, apiRecord], batches: batches.map((b) => (b.id === d.id ? saved : b)) })
        local(() => {
          hub().addHistory(who, slug, `API ${input.name}`, `Created: ${input.method} ${input.path} · ${path} + ${testsPathOf(route)} in batch ${d.version}`)
          hub().syncEnvFromManifest(slug, saved.manifest, who)
        })
        syncLender(slug, get().batches)
        send(slug, () => api<{ api: LenderApi; batchId: string; batch: Batch }>('POST', `/lenders/${enc(slug)}/apis`, { ...input, id: apiRecord.id, ...(madeDraft ? { batchId: madeDraft } : {}) }), (r) => { putApi(r.api); putBatch(r.batch) })
        return d.id
      },

      updateApi: (id, patch, who) => {
        const a = get().apis.find((x) => x.id === id)
        if (!a) return
        set({ apis: get().apis.map((x) => (x.id === id ? { ...x, ...patch } : x)) })
        local(() => hub().addHistory(who, a.lenderSlug, `API ${a.name}`, `Record changed: ${Object.keys(patch).join(', ')}`))
        const body = { ...patch } as Partial<LenderApi>
        delete body.id
        delete body.lenderSlug
        delete body.lastCall
        send(a.lenderSlug, () => api<LenderApi>('PATCH', `/apis/${enc(id)}`, body), putApi)
      },

      save: (batchId, files, tests, message, who, opts = {}) => {
        const b = get().batches.find((x) => x.id === batchId)
        if (!b) return
        let saved = applySave(b, files, tests, message, who, nowIso(), { sha: opts.sha, manifest: opts.manifest })
        if (opts.commitFiles?.length) saved = { ...saved, commits: [{ ...saved.commits[0]!, files: opts.commitFiles }, ...saved.commits.slice(1)] }
        set({ batches: get().batches.map((x) => (x.id === batchId ? saved : x)) })
        const c = saved.commits[0]!
        local(() => {
          hub().addHistory(who, b.lenderSlug, `Batch ${b.version} · ${c.files.join(', ') || 'files'}`, `Save: commit ${c.sha} “${c.message}”`)
          hub().syncEnvFromManifest(b.lenderSlug, saved.manifest, who)
        })
        syncLender(b.lenderSlug, get().batches)
        // the manifest the browser read on the text goes too when the sandbox did not discover one
        send(b.lenderSlug, () => api<Batch>('POST', `/batches/${enc(batchId)}/save`, { files, tests, message, manifest: saved.manifest, ...(opts.sha ? { sha: opts.sha, commitFiles: opts.commitFiles ?? null } : {}) }), putBatch)
      },

      setManifest: (batchId, manifest, who) => {
        const b = get().batches.find((x) => x.id === batchId)
        if (!b) return
        set({ batches: get().batches.map((x) => (x.id === batchId ? { ...x, manifest } : x)) })
        local(() => hub().syncEnvFromManifest(b.lenderSlug, manifest, who))
        send(b.lenderSlug, () => api<Batch>('PUT', `/batches/${enc(batchId)}/manifest`, manifest), putBatch)
      },

      recordTestRun: (batchId, results, who) => {
        const b = get().batches.find((x) => x.id === batchId)
        if (!b) throw new Error('batch not found')
        const next = applyTestRun(b, results, who, nowIso())
        set({ batches: get().batches.map((x) => (x.id === batchId ? next : x)) })
        local(() => hub().addHistory(who, b.lenderSlug, `Batch ${b.version}`, `Run all tests: ${next.testRun!.passed} / ${next.testRun!.total} passed${next.status === 'tests_passed' ? ' → tests passed' : ''}`))
        send(b.lenderSlug, () => api<Batch>('POST', `/batches/${enc(batchId)}/test-run`, { results }), putBatch)
        return next
      },

      build: async (batchId, who) => {
        const b = get().batches.find((x) => x.id === batchId)
        if (!b) throw new Error('batch not found')
        if (apiMode()) {
          // the real build takes a while (image, the tests inside it, the bundle, the tag); the screen waits for it
          await whenSynced()
          const built = await api<Batch>('POST', `/batches/${enc(batchId)}/build`, {})
          putBatch(built)
          await Promise.all([refreshLender(b.lenderSlug), refreshLog()]).catch(() => {})
          return built
        }
        const checksum = await checksumOf(b.files)
        const built = applyBuild(b, checksum, who, nowIso())
        set({ batches: get().batches.map((x) => (x.id === batchId ? built : x)) })
        hub().addHistory(who, b.lenderSlug, `Batch ${b.version}`, `Built: tag ${built.git.tag}, bundle ${built.bundle!.key}, ${checksum.slice(0, 16)}…`)
        syncLender(b.lenderSlug, get().batches)
        return built
      },

      makeLive: (batchId, who, moved) => {
        const b = get().batches.find((x) => x.id === batchId)
        if (!b) throw new Error('batch not found')
        const change = applyMakeLive(get().batches, batchId, nowIso())
        set({ batches: change.batches })
        local(() => {
          hub().addHistory(who, b.lenderSlug, `Batch ${b.version}`, `Made live${change.previous ? `; ${change.previous} is previous` : ''}${change.retired ? `; ${change.retired} retired` : ''}${moved.length ? `; moved: ${moved.join(', ')}` : ''}`)
          hub().addAlert({ lenderSlug: b.lenderSlug, severity: 'info', text: `Batch ${batchName(b)} is live (by ${who}); ${moved.length ? `${moved.length} live workflow(s) moved to it after the C1 check` : 'no live workflow follows the live batch'}.` })
        })
        syncLender(b.lenderSlug, get().batches)
        // (the screens use switchOnHub on the API: it waits for C1 and the runner's container)
        send(b.lenderSlug, () => api<{ batches: Batch[] }>('POST', `/batches/${enc(batchId)}/make-live`, {}), (r) => putBatches(b.lenderSlug, r.batches))
        return change
      },

      rollBack: (batchId, who, moved) => {
        const b = get().batches.find((x) => x.id === batchId)
        if (!b) throw new Error('batch not found')
        const change = applyRollBack(get().batches, batchId, nowIso())
        set({ batches: change.batches })
        local(() => {
          hub().addHistory(who, b.lenderSlug, `Batch ${b.version}`, `Rolled back to ${b.version}${change.previous ? ` (${change.previous} is previous)` : ''}${moved.length ? `; moved back: ${moved.join(', ')}` : ''}`)
          hub().addAlert({ lenderSlug: b.lenderSlug, severity: 'warning', text: `Roll-back: ${batchName(b)} is live again (by ${who}).` })
        })
        syncLender(b.lenderSlug, get().batches)
        send(b.lenderSlug, () => api<{ batches: Batch[] }>('POST', `/batches/${enc(batchId)}/roll-back`, {}), (r) => putBatches(b.lenderSlug, r.batches))
        return change
      },

      switchOnHub: async (batchId, kind) => {
        const b = get().batches.find((x) => x.id === batchId)
        if (!b) throw new Error('batch not found')
        await whenSynced()
        const r = await api<HubLiveChange>('POST', `/batches/${enc(batchId)}/${kind === 'live' ? 'make-live' : 'roll-back'}`, {})
        putBatches(b.lenderSlug, r.batches)
        await Promise.all([refreshLender(b.lenderSlug), refreshLog()]).catch(() => {})
        return r
      },

      newVersion: (slug, fromId, version, who, productSlug) => {
        const named = fromId ? get().batches.find((b) => b.id === fromId && b.lenderSlug === slug) : undefined
        const product = productSlug !== undefined ? productSlug : named?.productSlug ?? null
        const mine = ofCode(get().batches, slug, product)
        const from = (fromId ? mine.find((b) => b.id === fromId) : mine.find((b) => b.status === 'live')) ?? null
        if (fromId && !from) throw new Error(`Batch ${fromId} is not a version of ${product ?? slug}`)
        const v = version ?? nextVersion(mine)
        if (mine.some((b) => b.version === v)) throw new Error(`Version ${v} of ${product ?? slug} already exists`)
        const created = newVersionFrom(from, slug, v, who, nowIso(), product)
        set({ batches: [created, ...get().batches] })
        local(() => hub().addHistory(who, slug, `Batch ${product ? `${product} ` : ''}${v}`, `New version from ${from?.version ?? 'nothing'}: branch ${created.git.branch}`))
        syncLender(slug, get().batches)
        send(slug, () => api<Batch>('POST', `/lenders/${enc(slug)}/batches`, { fromId: from?.id ?? null, version: v, id: created.id, productSlug: product }), putBatch)
        return created
      },

      addTestEvent: (batchId, test, who) => {
        const b = get().batches.find((x) => x.id === batchId)
        if (!b) return
        const t = { ...test, id: newId('t') }
        const saved = applySave(b, b.files, [...b.tests, t], `Test event “${test.name}” for ${test.endpoint}`, who, nowIso())
        set({ batches: get().batches.map((x) => (x.id === batchId ? saved : x)) })
        local(() => hub().addHistory(who, b.lenderSlug, `Batch ${b.version} · tests/`, `Added test event “${test.name}” → ${test.expectedOutcome} (${test.endpoint})`))
        send(b.lenderSlug, () => api<Batch>('POST', `/batches/${enc(batchId)}/tests`, t), putBatch)
      },

      addCapturedAsTest: (capturedId, expectedOutcome, who, productSlug) => {
        const cap = get().captured.find((c) => c.id === capturedId)
        if (!cap) throw new Error('captured code not found')
        let draft = get().draftOf(cap.lenderSlug, productSlug)
        if (!draft) draft = get().newVersion(cap.lenderSlug, null, null, who, productSlug ?? null)
        const test = { endpoint: cap.endpoint, name: `${cap.code} (captured)`, reply: cap.sampleReply, expectedOutcome, source: 'captured' as const }
        if (apiMode()) {
          // one call: the API adds the test event to the draft (one commit) and acknowledges the alert
          const d = draft
          set({ batches: get().batches.map((x) => (x.id === d.id ? { ...x, tests: [...x.tests, { ...test, id: newId('t') }] } : x)), captured: get().captured.map((c) => (c.id === capturedId ? { ...c, known: 'known', outcome: expectedOutcome } : c)) })
          send(cap.lenderSlug, () => api<{ batchId: string; batch: Batch }>('POST', `/captured/${enc(capturedId)}/add-as-test`, { expectedOutcome, batchId: d.id }), (r) => putBatch(r.batch))
          send(cap.lenderSlug, () => api<CapturedCode[]>('GET', `/lenders/${enc(cap.lenderSlug)}/captured`), (list) => putCaptured(cap.lenderSlug, list))
          return d.id
        }
        get().addTestEvent(draft.id, test, who)
        set({ captured: get().captured.map((c) => (c.id === capturedId ? { ...c, known: 'known', outcome: expectedOutcome } : c)) })
        if (cap.alertId) hub().acknowledgeAlert(cap.alertId, who)
        return draft.id
      },

      captureCodes: (lenderSlug, items, env) => {
        const at = nowIso()
        const fresh: string[] = []
        let captured = get().captured
        for (const it of items) {
          const hit = captured.find((c) => c.lenderSlug === lenderSlug && c.code === it.code && c.endpoint === it.endpoint)
          if (hit) {
            captured = captured.map((c) => (c === hit ? { ...c, uat: c.uat + (env === 'uat' ? 1 : 0), prod: c.prod + (env === 'production' ? 1 : 0), lastSeen: at } : c))
            continue
          }
          const isNew = it.outcome === 'error'
          let alertId: string | null = null
          if (isNew) {
            if (!apiMode()) {
              hub().addAlert({ lenderSlug, severity: 'danger', kind: 'new_code', text: `New lender code ${it.code} on ${endpointLabel(it.endpoint)} (${env}); the handler returns error for it. Add it to its endpoint file and its test events.` })
              alertId = hub().alerts[0]!.id
            }
            fresh.push(it.code)
          }
          captured = [{ id: newId('cap'), lenderSlug, code: it.code, endpoint: it.endpoint, uat: env === 'uat' ? 1 : 0, prod: env === 'production' ? 1 : 0, lastSeen: at, known: isNew ? 'new' : 'known', outcome: isNew ? null : it.outcome, alertId, sampleReply: it.reply }, ...captured]
        }
        set({ captured })
        // on the API the runner captures the codes of every run itself (LH-B3): the Hub's list is read back
        if (apiMode()) send(lenderSlug, () => api<CapturedCode[]>('GET', `/lenders/${enc(lenderSlug)}/captured`), (list) => putCaptured(lenderSlug, list))
        return fresh
      },

      reset: () => {
        if (apiMode()) { void loadCodeFromHub(useHubStore.getState().lenders.map((l) => l.slug)); return }
        set({ apis: APIS, batches: BATCHES, captured: CAPTURED })
      },
    }),
    // the API's copy is never kept in this browser: it is read again at every sign-in
    { name: apiMode() ? 'lender-hub-portal-code-api' : 'lender-hub-portal-code', version: 1, partialize: (s) => (apiMode() ? ({} as Partial<CodeState>) : s) },
  ),
)
